import { memo, useMemo, useCallback } from 'react';
import { MessageCircle, Zap, Coins, BarChart3, LineChart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createTranslator } from '../../utils/detectLanguage';
import { homepagePtBR } from '../mappings/homepage.pt-br';
import { homepageES } from '../mappings/homepage.es';

const QuickActionsGrid = memo(({ user = null, showAuthPrompt }) => {
  const navigate = useNavigate();

  const t = useMemo(() => createTranslator({
    'pt-br': homepagePtBR,
    'es': homepageES
  }), []);

  const actions = useMemo(() => [
    {
      id: 'chat',
      icon: MessageCircle,
      title: 'New Chat',
      description: 'Ask your AI assistant',
      action: () => user ? navigate('/chat') : showAuthPrompt('Start chatting with AI', { isTitle: true })
    },
    {
      id: 'quickpicks',
      icon: Zap,
      title: 'QuickPicks',
      description: 'Fast AI predictions',
      action: () => user ? navigate('/football') : showAuthPrompt('Get AI predictions', { isTitle: true })
    },
    {
      id: 'buycoins',
      icon: Coins,
      title: 'Buy Coins',
      description: 'Get StarCoins for premium',
      action: () => user ? navigate('/upgrade') : showAuthPrompt('starCoins')
    },
    {
      id: 'aiexchange',
      icon: BarChart3,
      title: 'AI Exchange',
      description: 'Market insights & trends',
      action: () => user ? navigate('/market') : showAuthPrompt('predictions')
    },
    // Sports Hub removed - users can access sports directly from the horizontal sports bar
    {
      id: 'marketview',
      icon: LineChart,
      title: 'Market View',
      description: 'Browse active games',
      action: () => user ? navigate('/market') : showAuthPrompt('predictions')
    }
  ], [navigate, user, showAuthPrompt]);
  
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
      {actions.map((action) => {
        const Icon = action.icon;
        return (
          <button
            key={action.id}
            data-action-id={action.id}
            onClick={action.action}
            className="group relative flex flex-col border border-white/[0.08]
                     bg-slate-900/40 backdrop-blur-sm p-3 min-h-[80px] md:min-h-[90px]
                     transition-all duration-200 hover:scale-[1.01] hover:bg-slate-900/60
                     hover:border-emerald-500/30 active:scale-[0.98]"
          >
            {/* Terminal grid background */}
            <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
              style={{
                backgroundImage: `
                  linear-gradient(to right, #10b981 1px, transparent 1px),
                  linear-gradient(to bottom, #10b981 1px, transparent 1px)
                `,
                backgroundSize: '20px 20px'
              }}
            />

            {/* Content */}
            <div className="relative flex flex-col items-start text-left flex-1">
              <h3 className="text-xs font-medium text-white/50 uppercase tracking-[0.2em] mb-1 font-mono">
                {t(action.title)}
              </h3>
              <p className="text-[11px] text-white/70 leading-relaxed">
                {t(action.description)}
              </p>
            </div>

            {/* Icon - top right corner */}
            <Icon className="absolute top-2.5 right-2.5 h-4 w-4 text-white/30
                          group-hover:text-emerald-400/80 transition-colors duration-200"
                  strokeWidth={1.5} />
          </button>
        );
      })}
    </div>
  );
});

export default QuickActionsGrid;